"use client";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { FaLinkedinIn, FaFacebookF, FaGithub } from "react-icons/fa6";
import type { IconType } from "react-icons";
import { AppButton, Container } from "@/components";
import { TeamMember } from "@/lib/types";

interface TeamConnectProps {
  member: TeamMember;
}

const SOCIAL_ICONS: Record<string, IconType> = {
  LinkedIn: FaLinkedinIn,
  Facebook: FaFacebookF,
  GitHub: FaGithub,
};

// Closing block for the profile — a dark CTA that points to the contact page and
// back to the full team, with the member's own social links.
const TeamConnect = ({ member }: TeamConnectProps) => {
  const first = member.name.split(" ")[0];
  const socials = member.socials.filter((s) => s.platform in SOCIAL_ICONS);

  return (
    <section className="bg-card-foreground py-16 md:py-24">
      <Container className="px-4 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col items-center text-center"
        >
          <p className="text-sm font-semibold uppercase tracking-[0.25em] text-primary">
            Let&rsquo;s Build Something
          </p>
          <h2 className="mt-4 max-w-3xl font-black uppercase leading-[0.95] tracking-tighter text-4xl text-white md:text-6xl">
            WANT {first.toUpperCase()} ON YOUR NEXT PROJECT?
          </h2>
          <p className="mt-6 max-w-md text-base text-white/60">
            The whole Octarnal crew ships together — strategy, design and
            engineering under one roof.
          </p>

          {socials.length > 0 && (
            <div className="mt-8 flex gap-3">
              {socials.map((s) => {
                const Icon = SOCIAL_ICONS[s.platform];
                return (
                  <a
                    key={s.platform}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.platform}
                    className="grid h-11 w-11 place-items-center rounded-full border border-white/15 bg-white/5 text-white transition-all duration-300 hover:-translate-y-1 hover:border-primary hover:bg-primary hover:text-black"
                  >
                    <Icon className="h-4.5 w-4.5" />
                  </a>
                );
              })}
            </div>
          )}

          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <AppButton href="/contact" className="w-52" buttonClass="h-12">
              <span>Start a project</span>
              <ArrowUpRight className="h-4 w-4" />
            </AppButton>
            <AppButton
              href="/about-us#team"
              className="w-52"
              buttonClass="h-12 bg-transparent text-white"
              shadowClass="bg-white/20"
            >
              <span>Back to the team</span>
            </AppButton>
          </div>
        </motion.div>
      </Container>
    </section>
  );
};

export default TeamConnect;
